<template>
  <div v-if="integration && (integration.active || hasSyncError || hasNoSyncMessage)">
    <div class="integration-link-container" data-toggle="dropdown">
      <div class="integration-link-logo">
        <img :src="imagePath" />
      </div>
      <div class="integration-link-name">
        <i class="far fa-ellipsis-h fa-fw integration-icon-action" />
        {{name}}
      </div>
    </div>
    <ul class="dropdown-menu">
      <DropdownMenuItem icon="fa-wifi" disabled v-if="!hasNoSyncMessage">Status: {{status}}</DropdownMenuItem>
      <DropdownMenuItem icon="fa-exclamation-triangle" disabled v-if="hasNoSyncMessage">{{hasNoSyncMessage}}</DropdownMenuItem>
      <DropdownMenuItem icon="fa-external-link" v-if="integration.link" :link="integration.link">View Item</DropdownMenuItem>
      <DropdownMenuDivider v-if="canResyncItem || canForceSyncItem || canRelinkItem" />
      <DropdownMenuItem icon="fa-sync" v-if="canResyncItem" @click="onResyncItem">{{resyncLabel}}</DropdownMenuItem>
      <DropdownMenuItem icon="fa-sync" v-if="canForceSyncItem" @click="onForceSyncItem">{{forceSyncLabel}}</DropdownMenuItem>
      <DropdownMenuItem icon="fa-link" v-if="canRelinkItem" @click="onRelinkItem">{{relinkLabel}}</DropdownMenuItem>
      <DropdownMenuItem icon="fa-unlink" v-if="canUnlinkItem" @click="onUnlinkItem">{{unlinkLabel}}</DropdownMenuItem>
    </ul>
  </div>
</template>

<style scoped lang="scss">
$integration-logo-size: 20px;

.integration-link-container {
  width: 100%;
  display: flex;
  flex-direction: row;
  border: 1px solid #efefef;
  border-radius: 6px;
  cursor:pointer;
}
.integration-link-logo {
  width: calc(#{$integration-logo-size} + 8px);
  padding: 3px 4px;
}
.integration-link-logo > img {
  width: $integration-logo-size;
}
.integration-link-name {
  text-align: left;
  font-weight: bold;
  flex: 1;
  margin: auto 0;
  font-size: 11px;
  padding-left: 2px;
}
.integration-icon-action {
  visibility: hidden;
  float: right;
  margin-right: 6px;
  margin-top: 2px;
}
.integration-link-container:hover .integration-icon-action {
  visibility: visible;
}
</style>

<script>
import IntegrationUnlinkDialog from '@/modules/admin/views/integrationUnlinkDialog.vue'

export default {
  name: 'LinkIntegrationLink',
  props: {
    integration: Object
  },
  computed: {
    link() {
      if(!this.integration.link)
        return '#'
      return this.integration.link
    },
    name() {
      if(this.hasNoSyncMessage)
        return `Not Synced`

      return this.integration.name
    },
    status() {
      if(this.hasSyncError)
        return this.integration.errorMessage
      if(!this.integration.linked)
        return 'Pending Sync'
      return 'Synced'
    },
    hasSyncError() {
      return this.integration.syncError
    },
    hasNoSyncMessage() {
      return !this.integration.linked && !this.integration.syncError && this.integration.noSyncMessage
    },
    isItemLinked() {
      return this.integration.linked
    },
    canResyncItem() {
      if(!this.isItemLinked)
        return false
      if(this.integration && this.integration.options && this.integration.options.resync) {
        if(!this.integration.options.resync.enabled)
          return false

        if(!this.userHasUpdatePermission(this.integration.options.resync.permissionRequired))
          return false

        return true
      }
      return false
    },
    resyncLabel() {
      if(this.integration && this.integration.options && this.integration.options.resync) {
        return this.integration.options.resync.name
      }
      return 'Resync Item'
    },
    canForceSyncItem() {
      if(this.integration && this.integration.options && this.integration.options.forceSync) {
        if(!this.integration.options.forceSync.enabled)
          return false

        if(!this.userHasUpdatePermission(this.integration.options.forceSync.permissionRequired))
          return false

        return true
      }
      return false
    },
    forceSyncLabel() {
      if(this.integration && this.integration.options && this.integration.options.forceSync) {
        return this.integration.options.forceSync.name
      }
      return 'Force Sync Item'
    },
    canRelinkItem() {
      if(!this.isItemLinked)
        return false
      if(this.integration && this.integration.options && this.integration.options.relink) {
        if(!this.integration.options.relink.enabled)
          return false

        if(!this.userHasUpdatePermission(this.integration.options.relink.permissionRequired))
          return false

        return true
      }
      return false
    },
    canUnlinkItem() {
      if(!this.isItemLinked)
        return false
      if(this.integration && this.integration.options && this.integration.options.unlink) {
        if(!this.integration.options.unlink.enabled)
          return false

        if(!this.userHasUpdatePermission(this.integration.options.unlink.permissionRequired))
          return false

        return true
      }
      return false
    },
    relinkLabel() {
      if(this.integration && this.integration.options && this.integration.options.relink) {
        return this.integration.options.relink.name
      }
      return 'Relink Item'
    },
    unlinkLabel() {
      if(this.integration && this.integration.options && this.integration.options.unlink) {
        return this.integration.options.unlink.name
      }
      return 'Unlink Item'
    },
    imagePath() {
      return `/glass/integrations/${this.integration.imagePath}`
    }
  },
  methods: {
    onResyncItem() {
      let resyncLink = this.integration.options.resync.link
      if(!resyncLink)
        return

      this.displayProcessingDialog({
        title: 'Resyncing Item',
        message: 'Resyncing',
        icon: 'fa-sync',
        onBeforeOpen: (instance) => {
          this.$api.post(resyncLink)
          .then(() => {
            instance.hide()
            this.successToast('Successfully requested item resync')

            this.$emit('refresh')
          })
          .catch(err => {
            instance.hide()
            this.displayNetworkError(err, 'Error Syncing Item')
          })
        }
      }, {
        mountBody: true
      })
    },
    onForceSyncItem() {
      this.confirmMessage('Force Sync Item?', 'Are you sure you want to force sync this item? This may cause changes to the linked item in the integrated system.')
      .then(success => {
        if(success) {
          let forceSyncLink = this.integration.options.forceSync.link
          if(!forceSyncLink)
            return

          this.displayProcessingDialog({
            title: 'Syncing Item',
            message: 'Syncing',
            icon: 'fa-sync',
            onBeforeOpen: (instance) => {
              this.$api.post(forceSyncLink)
              .then(() => {
                instance.hide()
                this.successToast('Successfully requested item sync')

                this.$emit('refresh')
              })
              .catch(err => {
                instance.hide()
                this.displayNetworkError(err, 'Error Syncing Item')
              })
            }
          }, {
            mountBody: true
          })
          
        }
      })
    },
    onRelinkItem() {
      this.$emit('relink', {
        integration: this.integration
      })
    },
    onUnlinkItem() {
      this.displayModal(IntegrationUnlinkDialog, { integrationLinkData: this.integration })
      .then(result => {
        if(result) {
          this.$emit('refresh')
        }
      })
    }
  }
}
</script>